/**
 * CALC boolean-expression model.
 *
 * A small AST for the boolean subset of the engine's CALC grammar that the
 * wizard builds visually: AND/OR groups of field comparisons, with NOT and
 * nesting. The wizard reads/writes a single expression STRING (the
 * `CalcStep.expression`); this module converts between that string and the
 * AST.
 *
 *   generateCalc(ast)  → "<<a>> == 1 OR <<a>> == 2"
 *   parseCalc(str)     → { ast, ok }   (best-effort; ok:false when the string
 *                        uses arithmetic/ternary/field-RHS the wizard can't
 *                        represent — the raw box handles those instead)
 *
 * Modeled on the questionnaire logic-wizard's ifGenerator/ifParser, but
 * targets CALC operators (== != < <= > >=, AND OR NOT) and codebook fields.
 */

export type CalcOp = "==" | "!=" | "<" | "<=" | ">" | ">=" | "truthy"

/** A leaf term: `<<field>> op value`, or a bare boolean field when
 *  op === "truthy" (emits just `<<field>>`). */
export interface CalcComparison {
  kind: "cmp"
  /** Codebook field path, stored bare (no `<<…>>`). */
  field: string
  op: CalcOp
  /** Numeric / boolean literal compared against. Ignored when op === "truthy". */
  value: string
}

export interface CalcGroup {
  kind: "group"
  op: "AND" | "OR"
  children: CalcNode[]
}

export interface CalcNot {
  kind: "not"
  child: CalcNode
}

export interface CalcPlaceholder {
  kind: "placeholder"
}

export type CalcNode = CalcGroup | CalcComparison | CalcNot | CalcPlaceholder

export const COMPARISON_OPS: { value: CalcOp; label: string }[] = [
  { value: "==", label: "= equals" },
  { value: "!=", label: "≠ not equal" },
  { value: "<", label: "< less than" },
  { value: "<=", label: "≤ at most" },
  { value: ">", label: "> greater than" },
  { value: ">=", label: "≥ at least" },
  { value: "truthy", label: "is true (yes/no field)" },
]

export const emptyGroup = (op: "AND" | "OR" = "AND"): CalcGroup => ({
  kind: "group",
  op,
  children: [],
})

export const placeholder = (): CalcPlaceholder => ({ kind: "placeholder" })

export const newComparison = (field = ""): CalcComparison => ({
  kind: "cmp",
  field,
  op: "==",
  value: "",
})

// ============================================================
// Completeness — Apply is disabled until the tree is fully specified.
// ============================================================

export const isCalcComplete = (node: CalcNode): boolean => {
  switch (node.kind) {
    case "placeholder":
      return false
    case "cmp":
      if (!node.field.trim()) return false
      return node.op === "truthy" ? true : node.value.trim() !== ""
    case "not":
      return isCalcComplete(node.child)
    case "group":
      return node.children.length > 0 && node.children.every(isCalcComplete)
  }
}

// ============================================================
// Generator — AST → CALC string.
// ============================================================

const generateNode = (node: CalcNode): string => {
  switch (node.kind) {
    case "placeholder":
      return ""
    case "cmp":
      if (!node.field.trim()) return ""
      return node.op === "truthy"
        ? `<<${node.field}>>`
        : `<<${node.field}>> ${node.op} ${node.value.trim()}`
    case "not": {
      const inner = generateNode(node.child)
      if (!inner) return ""
      // A bare boolean field doesn't need parens; anything else (comparison
      // or group) does, since NOT is low precedence in the engine grammar.
      const bare = node.child.kind === "cmp" && node.child.op === "truthy"
      return bare ? `NOT ${inner}` : `NOT (${inner})`
    }
    case "group": {
      const parts = node.children
        .map((child) => {
          const s = generateNode(child)
          if (!s) return ""
          // Parenthesise a nested group with a different operator (mixing
          // AND/OR), so precedence is explicit.
          if (
            child.kind === "group" &&
            child.op !== node.op &&
            child.children.length > 1
          ) {
            return `(${s})`
          }
          return s
        })
        .filter(Boolean)
      return parts.join(` ${node.op} `)
    }
  }
}

export const generateCalc = (node: CalcNode): string => generateNode(node)

// ============================================================
// Parser — best-effort CALC string → AST (for re-opening the wizard).
// ============================================================

type Tok =
  | { t: "field"; v: string }
  | { t: "num"; v: string }
  | { t: "bool"; v: string }
  | { t: "op"; v: CalcOp }
  | { t: "and" }
  | { t: "or" }
  | { t: "not" }
  | { t: "lp" }
  | { t: "rp" }

const tokenize = (input: string): Tok[] => {
  const toks: Tok[] = []
  let i = 0
  const n = input.length
  while (i < n) {
    const c = input[i]
    if (/\s/.test(c)) {
      i++
      continue
    }
    if (c === "<" && input[i + 1] === "<") {
      const end = input.indexOf(">>", i + 2)
      if (end === -1) throw new Error("unterminated <<")
      toks.push({ t: "field", v: input.slice(i + 2, end).trim() })
      i = end + 2
      continue
    }
    if (c === "(") {
      toks.push({ t: "lp" })
      i++
      continue
    }
    if (c === ")") {
      toks.push({ t: "rp" })
      i++
      continue
    }
    const two = input.slice(i, i + 2)
    if (two === "==" || two === "!=" || two === "<=" || two === ">=") {
      toks.push({ t: "op", v: two as CalcOp })
      i += 2
      continue
    }
    if (c === "<" || c === ">") {
      toks.push({ t: "op", v: c as CalcOp })
      i++
      continue
    }
    const numMatch = input.slice(i).match(/^-?\d+(\.\d+)?/)
    if (numMatch) {
      toks.push({ t: "num", v: numMatch[0] })
      i += numMatch[0].length
      continue
    }
    const wordMatch = input.slice(i).match(/^[A-Za-z_]\w*/)
    if (wordMatch) {
      const w = wordMatch[0]
      const upper = w.toUpperCase()
      if (upper === "AND") toks.push({ t: "and" })
      else if (upper === "OR") toks.push({ t: "or" })
      else if (upper === "NOT") toks.push({ t: "not" })
      else if (w === "true" || w === "false") toks.push({ t: "bool", v: w })
      else throw new Error(`unexpected identifier "${w}"`)
      i += w.length
      continue
    }
    // Anything else (arithmetic +-*/, ternary ?:, functions, etc.) is outside
    // the boolean subset the wizard handles.
    throw new Error(`unexpected character "${c}"`)
  }
  return toks
}

export interface ParseResult {
  ast: CalcGroup
  ok: boolean
}

/** Parse a CALC string into the wizard AST. Returns `ok:false` (and a fresh
 *  empty group) for anything outside the boolean subset, or empty input. */
export const parseCalc = (input: string): ParseResult => {
  const trimmed = input.trim()
  if (!trimmed) return { ast: emptyGroup(), ok: true }
  try {
    const toks = tokenize(trimmed)
    let pos = 0
    const peek = (): Tok | undefined => toks[pos]
    const eat = (): Tok => toks[pos++]

    const parsePrimary = (): CalcNode => {
      const tk = peek()
      if (!tk) throw new Error("unexpected end")
      if (tk.t === "not") {
        eat()
        return { kind: "not", child: parseUnary() }
      }
      if (tk.t === "lp") {
        eat()
        const inner = parseOr()
        if (peek()?.t !== "rp") throw new Error("missing )")
        eat()
        return inner
      }
      if (tk.t === "field") {
        eat()
        const next = peek()
        if (next && next.t === "op") {
          eat()
          const rhs = peek()
          if (!rhs || (rhs.t !== "num" && rhs.t !== "bool")) {
            // field-vs-field or missing RHS — not representable in the wizard.
            throw new Error("unsupported comparison RHS")
          }
          eat()
          return { kind: "cmp", field: tk.v, op: next.v, value: rhs.v }
        }
        // Bare boolean field.
        return { kind: "cmp", field: tk.v, op: "truthy", value: "" }
      }
      throw new Error("unexpected token")
    }

    const parseUnary = (): CalcNode => parsePrimary()

    const parseAnd = (): CalcNode => {
      const first = parseUnary()
      if (peek()?.t !== "and") return first
      const children: CalcNode[] = [first]
      while (peek()?.t === "and") {
        eat()
        children.push(parseUnary())
      }
      return { kind: "group", op: "AND", children }
    }

    const parseOr = (): CalcNode => {
      const first = parseAnd()
      if (peek()?.t !== "or") return first
      const children: CalcNode[] = [first]
      while (peek()?.t === "or") {
        eat()
        children.push(parseAnd())
      }
      return { kind: "group", op: "OR", children }
    }

    const root = parseOr()
    if (pos !== toks.length) throw new Error("trailing tokens")

    // The UI root must be a group.
    const ast: CalcGroup =
      root.kind === "group" ? root : { kind: "group", op: "AND", children: [root] }
    return { ast, ok: true }
  } catch {
    return { ast: emptyGroup(), ok: false }
  }
}

// ============================================================
// Raw-box validation — a light heuristic for the typed expression. The engine
// is the real validator (errors surface on Apply/Run).
// ============================================================

export interface CalcValidity {
  ok: boolean
  error?: string
}

export const validateCalcExpression = (expr: string): CalcValidity => {
  const s = expr.trim()
  if (!s) return { ok: false, error: "Expression is empty." }
  const opens = (s.match(/<</g) ?? []).length
  const closes = (s.match(/>>/g) ?? []).length
  if (opens !== closes) {
    return { ok: false, error: "Unbalanced << >> around a field reference." }
  }
  let depth = 0
  for (const ch of s) {
    if (ch === "(") depth++
    else if (ch === ")") {
      depth--
      if (depth < 0) return { ok: false, error: "Unbalanced parentheses." }
    }
  }
  if (depth !== 0) return { ok: false, error: "Unbalanced parentheses." }
  if ((s.match(/"/g) ?? []).length % 2 !== 0) {
    return { ok: false, error: "Unbalanced quotes." }
  }
  return { ok: true }
}
